import { useLocation, useNavigate } from "react-router-dom";

import type { Actor } from "@src/actor";
import { IS_IN_IFRAME } from "@src/env";
import { useAppBusEvent } from "@src/features/sse";
import { navigation } from "@src/shared/navigation";

import { useIsInElationEHR } from "../machine";

interface UsePatientTranscriptionRedirectArgs {
  patientId: string | undefined;
  actor: Actor;
}

/**
 * In-iframe hook that listens for `encounter.transcription_created` on the
 * unified app SSE bus and, when the event matches the iframe's current
 * patient/physician and we're on the briefing route, redirects to /today
 * with the resolved encounter id.
 *
 * The same event drives the encounter actor (encounterActor.ts subscribes
 * to it as well) — that handler updates global encounter state. This hook
 * only handles the iframe-specific routing concern, layered on top.
 *
 * Gated to:
 *   - in-iframe (IS_IN_IFRAME)
 *   - Elation EHR (useIsInElationEHR)
 *   - physician actor (staff actors don't have a physicianId to match)
 *   - briefing route only (never overrides another in-app navigation)
 *   - same-patient (event.patientId must equal the iframe's patient)
 *   - same-physician (event.physicianId must equal actor.user.id) — the
 *     SSE channel is already physician-scoped server-side; this is a
 *     defensive belt-and-suspenders check
 */
export function usePatientTranscriptionRedirect({
  patientId,
  actor,
}: UsePatientTranscriptionRedirectArgs): void {
  const isInElationEHR = useIsInElationEHR();
  const location = useLocation();
  const navigate = useNavigate();

  const numericPatientId =
    patientId != null && Number.isFinite(Number(patientId))
      ? Number(patientId)
      : null;

  const enabled =
    IS_IN_IFRAME &&
    isInElationEHR &&
    actor.type === "physician" &&
    numericPatientId != null;

  useAppBusEvent("encounter.transcription_created", (event) => {
    if (!enabled) return;
    if (patientId == null || numericPatientId == null) return;
    if (event.patientId !== numericPatientId) return;
    if (actor.type !== "physician") return;
    if (event.physicianId !== actor.user.id) return;

    const briefingPath = navigation.patientBriefing(patientId);
    if (location.pathname !== briefingPath) return;

    // The encounter actor already received this same event synchronously
    // and set its active encounter id (TRANSCRIPTION_CREATED listener runs
    // before this one), so today's createEncounter findOrCreate will resolve
    // to the same id without needing a `?encounterId=` query hint. Avoiding
    // the query param also dodges a state-machine quirk: when today later
    // navigates to /encounter/:id, the search params drop from `?encounterId=N`
    // to empty and the actor's onSearchParamsChanged clears the id —
    // encounter.completed then misses matchesActiveEncounter and the page
    // gets stuck on "Generating".
    void navigate(navigation.patientToday(patientId), { replace: true });
  });
}
